/**
 * Documents API: the files hanging off a record (W-05 › Belgeler) and the
 * company's own drawer — the trade registry gazette, the lease, the permits
 * (şirket evrak arşivi, migration 20261022000001).
 *
 * Both halves are the same two writes — an object in the `documents` bucket and
 * a row in `public.documents` — so they share this file rather than drifting
 * apart. What separates them is `ref_type`: a company paper points at its own
 * company and lives under `<company_id>/company/…`, and that second path segment
 * is what the storage policy reads to decide that only owner, partner and the
 * accountant may be handed a signed URL for the tax file.
 *
 * Reading needs `can(role, 'accountingRead')`, writing `can(role, 'write')`; RLS
 * says the same, this is only what the screens gate on.
 */
import type { AslenClient, Enums, Tables, TablesUpdate } from '../db';
import { DOCUMENT_MAX_BYTES, documentFileName, fileExtension, mimeForName } from '../documents';
import { trFold } from '../text';
import { AppError, unwrap } from './errors';
import { objectPath, uploadFile, type Bucket } from './storage';

export { DOCUMENT_ACCEPT, DOCUMENT_MAX_BYTES, DOCUMENT_MIME_TYPES } from '../documents';

export type DocumentRow = Tables<'documents'>;
export type DocumentFolder = Tables<'document_folders'>;
export type DocumentKind = Enums<'document_kind'>;

/**
 * `ref_type` of the company's own paperwork. It points at the company row itself
 * (`documents_company_ref_check`), because the papers that say the company exists
 * have nothing else to hang from.
 */
export const COMPANY_DOC_REF = 'company';

/** Only for the size message, so the number and the limit cannot disagree. */
const MB = 1024 * 1024;

// ── upload ──────────────────────────────────────────────────────────────────
/**
 * One file, described the same way on both platforms: the web passes the `File`
 * itself, the phone passes the bytes it read out of the picker's cache. `mime` is
 * whatever the picker claimed and may well be empty or wrong — `mimeForName`
 * decides from the name.
 */
export interface UploadSource {
  name: string;
  mime: string;
  size: number;
  body: Blob | ArrayBuffer | Uint8Array;
  /**
   * The uuid the object key gets. Left out in the browser, where `objectPath`
   * defaults it from `crypto`; the phone has no such global and fills it in from
   * expo-crypto when it reads the file (apps/mobile/src/lib/files.ts).
   */
  id?: string;
}

/** What an `<input type="file">` hands over, in the shape upload wants. */
export function fileSource(file: File): UploadSource {
  return { name: file.name, mime: file.type, size: file.size, body: file };
}

export interface UploadDocumentInput {
  companyId: string;
  refType: string;
  refId: string;
  /** Defaults to the table's 'other'. */
  kind?: DocumentKind;
  /** Shown in the list; defaults to the file name. */
  title?: string;
  /** Which divider of the drawer. Stock and contact attachments need none. */
  folderId?: string | null;
  /** The one date on a paper the owner has to act on (lease, permit, policy). */
  validUntil?: string | null;
  note?: string | null;
  source: UploadSource;
}

/**
 * Uploads to the `documents` bucket and files the row. The storage object comes
 * first: a row pointing at a file that failed to upload would show a broken link,
 * while an orphan object is invisible. The bucket's own MIME and size limits are
 * checked here so the user gets a Turkish message instead of a storage error.
 */
export async function uploadDocument(client: AslenClient, input: UploadDocumentInput): Promise<DocumentRow> {
  const { source } = input;
  if (source.size > DOCUMENT_MAX_BYTES) {
    throw new AppError(`Dosya ${Math.round(DOCUMENT_MAX_BYTES / MB)} MB’tan büyük olamaz.`, 'rule', 'file_too_large');
  }
  const mime = mimeForName(source.name, source.mime);
  if (!mime) {
    // never fall back to application/octet-stream: the bucket would reject it and
    // the user would be told nothing about *which* file it refused
    const ext = fileExtension(source.name);
    throw new AppError(
      ext ? `Bu dosya türü yüklenemiyor: .${ext}` : 'Bu dosyanın türü anlaşılamadı — uzantısı olan bir dosya seç.',
      'rule',
      'file_type',
    );
  }
  // the company drawer has no third segment: <company_id>/company/<uuid>-<name>,
  // which is what documents_company_path_check and storage_select both expect
  const entity = input.refType === COMPANY_DOC_REF ? COMPANY_DOC_REF : `${input.refType}s/${input.refId}`;
  const path = objectPath(input.companyId, entity, source.name, source.id);
  await uploadFile(client, 'documents', path, source.body, mime);
  try {
    return unwrap(
      await client
        .from('documents')
        .insert({
          company_id: input.companyId,
          ref_type: input.refType,
          ref_id: input.refId,
          kind: input.kind,
          title: input.title?.trim() || source.name,
          folder_id: input.folderId ?? null,
          valid_until: input.validUntil ?? null,
          note: input.note?.trim() || null,
          storage_bucket: 'documents',
          storage_path: path,
          mime_type: mime,
          size_bytes: source.size,
        })
        .select()
        .single(),
    );
  } catch (err) {
    // the row is what makes the object findable, so an object without one is junk.
    // Try to take it back; if the bucket will not let us, stay quiet about that —
    // the error the user has to see is the one that lost their upload.
    await client.storage
      .from('documents')
      .remove([path])
      .catch(() => undefined);
    throw err;
  }
}

/**
 * The fields the drawer may change. `ref_type`, `ref_id` and `storage_path` are
 * refused by `private.document_before` — moving a paper between dividers is
 * allowed, changing what it is a paper *of* is not.
 */
export type DocumentPatch = Pick<TablesUpdate<'documents'>, 'title' | 'kind' | 'folder_id' | 'valid_until' | 'note'>;

export async function updateDocument(client: AslenClient, id: string, patch: DocumentPatch): Promise<DocumentRow> {
  return unwrap(await client.from('documents').update(patch).eq('id', id).select().single());
}

/**
 * Soft delete — and the only delete there is. Neither table has a DELETE policy:
 * a company that cannot produce last year's lease has a problem this app should
 * not help it create, so an archived paper keeps its row and its audit trail.
 */
export async function archiveDocument(client: AslenClient, id: string): Promise<void> {
  unwrap(await client.from('documents').update({ deleted_at: new Date().toISOString() }).eq('id', id).select('id'));
}

// ── reading ─────────────────────────────────────────────────────────────────
/** Files attached to one record (`ref_type` 'item', 'lot', 'bom', …), newest first. */
export async function listDocuments(client: AslenClient, refType: string, refId: string): Promise<DocumentRow[]> {
  return unwrap(
    await client.from('documents').select('*').eq('ref_type', refType).eq('ref_id', refId).is('deleted_at', null).order('created_at', { ascending: false }),
  );
}

/**
 * The whole drawer, newest paper first. One request for every divider: nine
 * folders' worth of company paperwork is a list a phone can hold, and the screen
 * groups it without going back to the server per folder.
 */
export async function listCompanyDocuments(client: AslenClient, companyId: string): Promise<DocumentRow[]> {
  return unwrap(
    await client
      .from('documents')
      .select('*')
      .eq('company_id', companyId)
      .eq('ref_type', COMPANY_DOC_REF)
      .is('deleted_at', null)
      .order('created_at', { ascending: false }),
  );
}

// ── folders ─────────────────────────────────────────────────────────────────
/** Ordered by `sort`, because a label's alphabet is not its importance. */
export async function listDocumentFolders(client: AslenClient, companyId: string): Promise<DocumentFolder[]> {
  return unwrap(
    await client.from('document_folders').select('*').eq('company_id', companyId).is('deleted_at', null).order('sort').order('name'),
  );
}

export interface SaveDocumentFolderInput {
  companyId: string;
  /** Omitted for a new divider. */
  id?: string;
  name: string;
  /** Where it sits in the list; the nine seeded dividers hold 10…90, so 100 is the end. */
  sort?: number;
}

export async function saveDocumentFolder(client: AslenClient, input: SaveDocumentFolderInput): Promise<DocumentFolder> {
  const name = input.name.trim();
  if (!name) throw new AppError('Klasör adı boş olamaz.', 'rule', 'folder_name');
  // The unique index folds the name the Turkish way (private.tr_fold, not lower),
  // so "Banka" and "banka" are one divider. Caught here, with the existing name in
  // the message, rather than left to surface as the generic "Bu kayıt zaten var" —
  // and PostgREST cannot upsert onto an expression index anyway.
  const clash = (await listDocumentFolders(client, input.companyId)).find((f) => f.id !== input.id && trFold(f.name) === trFold(name));
  if (clash) throw new AppError(`“${clash.name}” klasörü zaten var.`, 'conflict', 'folder_exists');
  if (input.id) {
    const patch = input.sort === undefined ? { name } : { name, sort: input.sort };
    return unwrap(await client.from('document_folders').update(patch).eq('id', input.id).select().single());
  }
  return unwrap(
    await client.from('document_folders').insert({ company_id: input.companyId, name, sort: input.sort ?? 100 }).select().single(),
  );
}

/**
 * Archives an empty divider. A folder still holding papers is refused rather than
 * emptied: `documents.folder_id` has no cascade and a paper nobody filed is a
 * paper nobody finds. The nine seeded dividers stay — they are the filing system,
 * not a choice somebody made.
 */
export async function archiveDocumentFolder(client: AslenClient, id: string): Promise<void> {
  const folder = unwrap(await client.from('document_folders').select('is_system, name').eq('id', id).single());
  if (folder.is_system) throw new AppError(`“${folder.name}” hazır bir klasör, kaldırılamaz.`, 'rule', 'folder_is_system');
  const inside = unwrap(await client.from('documents').select('id').eq('folder_id', id).is('deleted_at', null).limit(1));
  if (inside.length > 0) {
    throw new AppError('Klasörde belge var — önce belgeleri başka klasöre taşı ya da arşivle.', 'rule', 'folder_not_empty');
  }
  unwrap(await client.from('document_folders').update({ deleted_at: new Date().toISOString() }).eq('id', id).select('id'));
}

// ── the file itself ─────────────────────────────────────────────────────────
/** `title` only matters for a download, where it is what names the saved file. */
type StoredFile = Pick<DocumentRow, 'storage_bucket' | 'storage_path'> & { title?: string };

const bucketOf = (doc: StoredFile): Bucket => (doc.storage_bucket || 'documents') as Bucket;

/**
 * A signed URL, one hour by default — nothing in this bucket is public. Not
 * `signedUrl()` from api/storage.ts, because that one cannot name the saved file
 * and the name is the point: the object key carries a uuid prefix, so a download
 * would land as "a1b2c3d4-…-kira-sozlesmesi.pdf" in the user's folder. The name
 * is built from `title` and the stored extension (`documentFileName`) rather than
 * read off the key, which no longer spells the Turkish the owner typed.
 */
export async function documentUrl(client: AslenClient, doc: StoredFile, opts: { seconds?: number; download?: boolean } = {}): Promise<string> {
  const data = unwrap(
    await client.storage
      .from(bucketOf(doc))
      .createSignedUrl(
        doc.storage_path,
        opts.seconds ?? 3600,
        opts.download ? { download: documentFileName(doc.title, doc.storage_path) } : undefined,
      ),
  );
  return data.signedUrl;
}

/**
 * The bytes. The phone shares and previews a *local* file, not a URL, so it has
 * to pull the object down first; the web only needs this for the rare save that
 * cannot go through a signed URL.
 */
export async function documentBlob(client: AslenClient, doc: StoredFile): Promise<Blob> {
  return unwrap(await client.storage.from(bucketOf(doc)).download(doc.storage_path));
}
